import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  UnorderedList,
  ListItem,
  UseModalProps,
  VStack,
} from '@chakra-ui/react'
import { StrictSwitchSummary } from '@rolebase/shared/helpers/getStrictSwitchSummary'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

// What the window hands back on confirmation: the canonical roles the person
// chose to create, with their (possibly renamed) name — never a lead link,
// never a comparison with what already exists (see getStrictSwitchSummary).
export interface StrictGovernanceRoleToCreate {
  name: string
  parentLink: boolean
  singleMember: boolean
  colorHue: number | null
}

// What actually made it to the database, whether onConfirm finished the
// whole journey or stopped partway on a failure. The modal uses this to
// uncheck those roles: a retry must only attempt what is still missing,
// never recreate what already exists.
export interface StrictConfirmResult {
  createdNames: string[]
}

interface Props extends UseModalProps {
  summary: StrictSwitchSummary
  // Resolves once the roles are created and the mode has switched, or once a
  // failure has been reported and it's safe to retry. The modal only
  // collects input and reports it: it neither creates a role nor calls
  // setGovernanceMode itself.
  onConfirm(
    rolesToCreate: StrictGovernanceRoleToCreate[]
  ): Promise<StrictConfirmResult>
}

interface ProposalState {
  checked: boolean
  name: string
}

// The confirmation shown before an org enters Strict governance: what
// changes, what stays open, the current state of its leadership, and an
// (entirely optional) chance to create the base roles it doesn't have yet.
// A render only — see getStrictSwitchSummary for the reasoning behind what it
// shows, and OrgSettingsPage for what happens with what it returns.
export default function StrictGovernanceModal({
  summary,
  onConfirm,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  // The four canonical roles start unchecked, with their default name — the
  // cadrage's D3: only a human can tell whether an existing role already
  // plays that part.
  const [proposals, setProposals] = useState<ProposalState[]>(() =>
    summary.proposedRoles.map((role) => ({ checked: false, name: role.name }))
  )

  const handleToggle = useCallback((index: number, checked: boolean) => {
    setProposals((prev) =>
      prev.map((proposal, i) =>
        i === index ? { ...proposal, checked } : proposal
      )
    )
  }, [])

  // Unchecking never clears the name: the person may tick it back on later.
  const handleRename = useCallback((index: number, name: string) => {
    setProposals((prev) =>
      prev.map((proposal, i) =>
        i === index ? { ...proposal, name } : proposal
      )
    )
  }, [])

  const handleConfirm = useCallback(async () => {
    setLoading(true)
    try {
      const rolesToCreate: StrictGovernanceRoleToCreate[] = proposals
        .map((proposal, index) => ({
          proposal,
          seed: summary.proposedRoles[index],
        }))
        .filter(({ proposal }) => proposal.checked)
        .map(({ proposal, seed }) => ({
          name: proposal.name,
          parentLink: seed.parentLink,
          singleMember: seed.singleMember,
          colorHue: seed.colorHue ?? null,
        }))
      const { createdNames } = await onConfirm(rolesToCreate)
      if (createdNames.length > 0) {
        // Uncheck whatever made it to the database: on success the modal
        // closes right behind this, so it's moot; on a failure partway
        // through, this is what stops a retry from recreating roles that
        // already exist.
        setProposals((prev) =>
          prev.map((proposal) =>
            createdNames.includes(proposal.name)
              ? { ...proposal, checked: false }
              : proposal
          )
        )
      }
    } finally {
      setLoading(false)
    }
  }, [proposals, summary.proposedRoles, onConfirm])

  const concernedCount =
    summary.circlesWithoutLeadLink.names.length +
    summary.circlesWithoutLeadLink.remainingCount

  return (
    <Modal size="xl" isCentered {...modalProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('StrictGovernanceModal.title')}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={6}>
            {/* The term is used by both columns and by the state of play below,
                so it is defined once, before its first use (Yohan, 09/08). */}
            <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.300' }}>
              {t('StrictGovernanceModal.linkRoleHint')}
            </Text>

            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Heading size="sm" mb={2}>
                  {t('StrictGovernanceModal.changesHeading')}
                </Heading>
                <UnorderedList spacing={1}>
                  <ListItem>{t('StrictGovernanceModal.change1')}</ListItem>
                  <ListItem>{t('StrictGovernanceModal.change2')}</ListItem>
                  <ListItem>{t('StrictGovernanceModal.change3')}</ListItem>
                </UnorderedList>
              </Box>
              <Box>
                <Heading size="sm" mb={2}>
                  {t('StrictGovernanceModal.openHeading')}
                </Heading>
                <UnorderedList spacing={1}>
                  <ListItem>{t('StrictGovernanceModal.open1')}</ListItem>
                  <ListItem>{t('StrictGovernanceModal.open2')}</ListItem>
                  <ListItem>{t('StrictGovernanceModal.open3')}</ListItem>
                </UnorderedList>
              </Box>
            </SimpleGrid>

            {concernedCount > 0 && (
              // status="info", not "warning": D5 of the cadrage (see
              // docs/features/gouv-03-passage-en-stricte.md) chose to say
              // what stays open rather than list what's forbidden, because
              // it's the fear of getting stuck that makes people give up,
              // and that fear is unfounded. An alarm-colored box here would
              // undercut that on the one state this window actually flags.
              <Alert status="info" flexDirection="column" alignItems="start">
                <HStack>
                  <AlertIcon />
                  <Heading size="sm">
                    {t('StrictGovernanceModal.stateHeading')}
                  </Heading>
                </HStack>
                <AlertDescription>
                  <Text mb={1}>
                    {t('StrictGovernanceModal.circlesWithoutLeadLink', {
                      count: concernedCount,
                    })}
                  </Text>
                  <UnorderedList mb={2}>
                    {summary.circlesWithoutLeadLink.names.map((name, index) => (
                      // A role can be reused across several circles, so two
                      // names in this list can be identical — the index is
                      // what actually makes the key unique, the name alone
                      // does not.
                      <ListItem key={`${name}-${index}`}>{name}</ListItem>
                    ))}
                  </UnorderedList>
                  {summary.circlesWithoutLeadLink.remainingCount > 0 && (
                    <Text fontStyle="italic" mb={2}>
                      {t('StrictGovernanceModal.circlesWithoutLeadLinkMore', {
                        count: summary.circlesWithoutLeadLink.remainingCount,
                      })}
                    </Text>
                  )}
                  <Text fontSize="sm">
                    {t('StrictGovernanceModal.circlesWithoutLeadLinkHint')}
                  </Text>
                </AlertDescription>
              </Alert>
            )}

            <Box>
              <Heading size="sm" mb={2}>
                {t('StrictGovernanceModal.baseRolesHeading')}
              </Heading>
              {summary.existingBaseRoles.length > 0 ? (
                <Text>
                  {summary.existingBaseRoles
                    .map((role) => role.name)
                    .join(' · ')}
                </Text>
              ) : (
                <Text fontStyle="italic">
                  {t('StrictGovernanceModal.baseRolesEmpty')}
                </Text>
              )}
            </Box>

            <Box>
              <Heading size="sm" mb={2}>
                {t('StrictGovernanceModal.proposedRolesHeading')}
              </Heading>
              <VStack align="stretch" spacing={2}>
                {proposals.map((proposal, index) => (
                  <HStack key={summary.proposedRoles[index].name}>
                    <Checkbox
                      isChecked={proposal.checked}
                      onChange={(e) => handleToggle(index, e.target.checked)}
                    />
                    <Input
                      size="sm"
                      value={proposal.name}
                      onChange={(e) => handleRename(index, e.target.value)}
                      autoComplete="off"
                    />
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={modalProps.onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="blue"
            isLoading={loading}
            onClick={handleConfirm}
          >
            {t('StrictGovernanceModal.confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
